import { useEffect, useState, useSyncExternalStore } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { cn } from '@/lib/utils/classNames';
import { InlineSpinner } from '@/components/Spinner';
import {
  getBlockingHeavyRefreshCount,
  getInFlightHeavyRefreshCount,
  getLastStaleServedAt,
  subscribeHeavyRefresh,
} from '@/lib/utils/indexedDbCache';

/** A stale-first paint older than this is no longer "the swap that's coming". */
const STALE_VIEW_WINDOW_MS = 60_000;
/** Don't flash the pill for refreshes that finish almost immediately. */
const SHOW_DELAY_MS = 400;

interface CacheRefreshIndicatorProps {
  /** Catalog/demo only: render the pill statically instead of tracking the cache. */
  demo?: boolean;
}

/**
 * Small "updating in the background…" pill, fixed under the header. Shows only while a
 * heavy-cache refresh is in flight *for data a page just painted stale-first* — so the
 * user has a reason for the row swap that follows — and never while a PageLoader is
 * already explaining a blocked wait. Proactive warming on a page that showed nothing
 * stale stays silent (the header refresh icon still spins for that).
 */
export function CacheRefreshIndicator({ demo = false }: CacheRefreshIndicatorProps) {
  const { t } = useI18nText();
  const backgroundRefreshing = useSyncExternalStore(
    subscribeHeavyRefresh,
    () => getInFlightHeavyRefreshCount() > 0 && getBlockingHeavyRefreshCount() === 0,
    () => false,
  );
  // Subscribed separately (a plain number snapshot) so a stale-first paint re-renders this
  // component even when the in-flight flag was already true.
  const lastStaleServedAt = useSyncExternalStore(subscribeHeavyRefresh, getLastStaleServedAt, () => 0);
  const explainsStaleView = Date.now() - lastStaleServedAt < STALE_VIEW_WINDOW_MS;
  const active = demo || (backgroundRefreshing && explainsStaleView);

  const [visible, setVisible] = useState(demo);
  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    const id = window.setTimeout(() => setVisible(true), demo ? 0 : SHOW_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [active, demo]);

  if (!visible) return null;

  return (
    <div
      className={cn('cache-refresh-indicator ggc-text-caption-s', demo && 'cache-refresh-indicator--static')}
      role="status"
      aria-live="polite"
    >
      <InlineSpinner size={12} />
      <span>{t('Updating data in the background...', '백그라운드에서 데이터를 갱신하는 중...')}</span>
    </div>
  );
}
